import React from "react";
import { graphql } from "gatsby";
import Section, { Alignment, BackgroundColour } from "@unum/section";
import Spring from "@unum/spring";
import { SimpleSectionFragmentFragment } from "../../graphql-types";
import RichText from "./RichText";
import { Theme } from "./Sections";
import styles from "../styles/SimpleSection.module.scss";
import { backgroundButtonMap } from "../utils/background";
import renderSpotlightContent from "../utils/render-spotlight-content";
import classnames from "classnames";

type Props = {
  data: SimpleSectionFragmentFragment;
  theme?: Theme;
  allowNested?: boolean;
};

const SimpleSection = ({ data, theme, allowNested = false }: Props) => {
  const { overline, title, body, align, background__background } =
    data.elements;

  const backgroundColour = (background__background.value[0]?.codename ||
    theme?.backgroundColour ||
    "transparent") as BackgroundColour;

  const richTextSettings = {
    rank: backgroundButtonMap[backgroundColour]
  };

  const alignment = align.value[0]?.codename;

  return (
    <Section
      {...theme}
      align={(alignment || "left") as Alignment}
      backgroundColour={backgroundColour}
      className={classnames(styles["SimpleSection"], "SimpleSectionAsRichText")}
      allowNested={allowNested}
    >
      <Spring allowNested={allowNested}>
        <div className="row" data-kontent-item-id={data.system.id}>
          <div
            className={classnames({
              [styles[`align_${alignment}`]]: alignment,
              ["col-md-offset-2"]: alignment === "center",
              ["col-md-8"]: !allowNested,
              ["col-xs-12"]: !allowNested
            })}
          >
            <Section.Heading overline={overline?.value} level={3} isWide>
              <div
                className={classnames({
                  [styles["heading--align_center"]]: alignment === "center"
                })}
              >
                {renderSpotlightContent(title.value, "title")}
              </div>
            </Section.Heading>
            {body && (
              <Section.Content>
                {renderSpotlightContent(
                  <RichText
                    codename="body"
                    data={body}
                    settings={richTextSettings}
                  />,
                  "body"
                )}
              </Section.Content>
            )}
          </div>
        </div>
      </Spring>
    </Section>
  );
};

export default SimpleSection;

export const query = graphql`
  fragment SimpleSectionFragment on kontent_item_simple_section {
    __typename
    system {
      id
    }
    elements {
      overline {
        value
      }
      title {
        value
      }
      align {
        value {
          codename
        }
      }
      background__background {
        value {
          codename
        }
      }
      body {
        ...RichTextFragment
      }
    }
  }
`;
